// src/pages/Officer/components/ConditionMonitor.jsx
import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Chip,
  Collapse,
  IconButton,
  Button,
  alpha,
} from '@mui/material';
import {
  Timeline,
  ExpandMore,
  ExpandLess,
  AccessTime,
  Circle,
  KeyboardArrowDown,
} from '@mui/icons-material';

/**
 * ConditionUpdateItem - Individual condition update display
 */
const ConditionUpdateItem = ({ update }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <Paper
      elevation={0}
      sx={{
        bgcolor: alpha('#0f1419', 0.6),
        border: '1px solid',
        borderColor: alpha('#666', 0.2),
        borderRadius: 2,
        overflow: 'hidden',
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: alpha('#00d4ff', 0.3),
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 1.5,
          cursor: 'pointer',
          '&:hover': {
            bgcolor: alpha('#00d4ff', 0.03),
          },
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Circle sx={{ fontSize: 8, color: '#00d4ff' }} />
            <Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: 'grey.300',
                  fontFamily: 'monospace',
                }}
              >
                {update.strategy_id}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.3 }}>
                <AccessTime sx={{ fontSize: 10, color: 'grey.600' }} />
                <Typography
                  variant="caption"
                  sx={{ color: 'grey.600', fontFamily: 'monospace', fontSize: '0.65rem' }}
                >
                  {new Date(update.timestamp).toLocaleTimeString()}
                </Typography>
              </Stack>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              label={`${Object.keys(update.condition_results || {}).length} conditions`}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                fontWeight: 600,
                bgcolor: alpha('#00d4ff', 0.1),
                color: '#00d4ff',
                border: '1px solid',
                borderColor: alpha('#00d4ff', 0.2),
              }}
            />
            {expanded ? (
              <ExpandLess sx={{ fontSize: 18, color: 'grey.600' }} />
            ) : (
              <ExpandMore sx={{ fontSize: 18, color: 'grey.600' }} />
            )}
          </Stack>
        </Stack>
      </Box>

      {/* Details */}
      <Collapse in={expanded}>
        <Box sx={{ p: 2, pt: 0 }}>
          <Stack spacing={1.5}>
            {update.condition_results &&
              Object.entries(update.condition_results).map(([name, result]) => (
                <Box
                  key={name}
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    bgcolor: alpha('#000', 0.3),
                    border: '1px solid',
                    borderColor: alpha('#666', 0.1),
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'grey.500',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.1em',
                      fontSize: '0.6rem',
                      display: 'block',
                      mb: 1,
                    }}
                  >
                    {name}
                  </Typography>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    {result.waiting && (
                      <Chip
                        label="Waiting"
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: '0.65rem',
                          fontWeight: 600,
                          bgcolor: alpha('#ffaa00', 0.15),
                          color: '#ffaa00',
                          border: '1px solid',
                          borderColor: alpha('#ffaa00', 0.3),
                        }}
                      />
                    )}
                    {result.trigger_active && (
                      <Chip
                        label="Trigger Active"
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: '0.65rem',
                          fontWeight: 600,
                          bgcolor: alpha('#00d4ff', 0.15),
                          color: '#00d4ff',
                          border: '1px solid',
                          borderColor: alpha('#00d4ff', 0.3),
                        }}
                      />
                    )}
                    {result.target_active && (
                      <Chip
                        label="Target Active"
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: '0.65rem',
                          fontWeight: 600,
                          bgcolor: alpha('#00ff88', 0.15),
                          color: '#00ff88',
                          border: '1px solid',
                          borderColor: alpha('#00ff88', 0.3),
                        }}
                      />
                    )}
                    {(result.condition_met || result.was_met) && (
                      <Chip
                        label="✓ Met"
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          bgcolor: alpha('#00ff88', 0.2),
                          color: '#00ff88',
                          border: '1px solid',
                          borderColor: alpha('#00ff88', 0.4),
                        }}
                      />
                    )}
                  </Stack>
                </Box>
              ))}
          </Stack>
        </Box>
      </Collapse>
    </Paper>
  );
};

/**
 * ConditionMonitor - Display condition status updates with pagination
 */
const ConditionMonitor = ({ updates }) => {
  const [displayCount, setDisplayCount] = useState(10);

  // Sort updates by timestamp descending (newest first)
  const sortedUpdates = [...updates].sort((a, b) => {
    const dateA = new Date(a.timestamp || 0);
    const dateB = new Date(b.timestamp || 0);
    return dateB - dateA; // Descending order (newest first)
  });

  const visibleUpdates = sortedUpdates.slice(0, displayCount);
  const hasMore = sortedUpdates.length > displayCount;

  const handleShowMore = () => {
    setDisplayCount(prev => Math.min(prev + 10, sortedUpdates.length));
  };

  return (
    <Paper
      elevation={0}
      sx={{
        height: 600,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: alpha('#0a0e14', 0.8),
        border: '1px solid',
        borderColor: alpha('#00d4ff', 0.1),
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid',
          borderColor: alpha('#00d4ff', 0.1),
          bgcolor: alpha('#0f1419', 0.6),
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1,
              bgcolor: alpha('#00d4ff', 0.1),
              border: '1px solid',
              borderColor: alpha('#00d4ff', 0.2),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Timeline sx={{ color: '#00d4ff', fontSize: 18 }} />
          </Box>
          <Box>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, color: 'grey.50' }}
            >
              Condition Monitor
            </Typography>
            <Typography variant="caption" sx={{ color: 'grey.600' }}>
              Showing {visibleUpdates.length} of {sortedUpdates.length} updates • Last 24 hours
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Updates List */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 2,
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            bgcolor: alpha('#000', 0.2),
            borderRadius: 1,
          },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: alpha('#00d4ff', 0.3),
            borderRadius: 1,
            '&:hover': {
              bgcolor: alpha('#00d4ff', 0.5),
            },
          },
        }}
      >
        {sortedUpdates.length === 0 ? (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Timeline sx={{ fontSize: 48, color: 'grey.800', mb: 2 }} />
            <Typography variant="body2" sx={{ color: 'grey.600', fontWeight: 600 }}>
              No updates yet
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: 'grey.700', textAlign: 'center', maxWidth: 280, mt: 1 }}
            >
              Condition status updates will appear here
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1.5}>
            {visibleUpdates.map((update) => (
              <ConditionUpdateItem key={update.id} update={update} />
            ))}
            
            {/* Show More Button */}
            {hasMore && (
              <Button
                onClick={handleShowMore}
                fullWidth
                variant="outlined"
                endIcon={<KeyboardArrowDown />}
                sx={{
                  py: 1.5,
                  borderColor: alpha('#00d4ff', 0.3),
                  color: '#00d4ff',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  '&:hover': {
                    borderColor: '#00d4ff',
                    bgcolor: alpha('#00d4ff', 0.05),
                  },
                }}
              >
                Show More ({sortedUpdates.length - displayCount} remaining)
              </Button>
            )}
          </Stack>
        )}
      </Box>
    </Paper>
  );
};

export default ConditionMonitor;
